import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle } from "lucide-react";
import {
  listingsApi,
  type CreateListingPayload,
  type ListingAgentDto,
  type ListingDetailDto,
  type ListingDuplicateDto,
  type ListingWritePayload,
  type UpdateListingPayload,
} from "@/api/buyers";
import {
  Button,
  FormField,
  Input,
  Textarea,
  useToast,
} from "@/design-system/components/ui";
import { Select } from "@/components/ui/Select";
// Modal is a staff-shell component, not part of the shared portal kit.
import { Modal } from "@/components/ui/Modal";
import { ListingAgentPicker } from "@/components/buyers/ListingAgentPicker";
import { formatCurrency } from "@/design-system/lib/format";
import { listingKeys } from "@/hooks/buyers/buyerKeys";
import { US_STATES } from "@/lib/usStates";
import { listingNameOrFallback, listingShortNameOrFallback } from "@/lib/listingDisplay";
import { ListingAgentEditModal } from "./ListingAgentDirectory";

/**
 * Create or correct a listing.
 *
 * <p>One modal for both, branching on whether it was handed a listing —
 * because the field set IS the same set, and two components would drift.</p>
 *
 * <p>🔴 <b>The two payloads do NOT share write semantics.</b> On PATCH, a
 * string field reads null=unchanged / ""=clear, so a hydrated edit form must
 * send every field or clearing one becomes impossible. On POST there is
 * nothing to clear, so blanks are omitted. Sending ""s to create would write
 * empty strings where the column should stay null.</p>
 */
export function ListingEditModal({
  open,
  listing,
  onClose,
  onSaved,
}: {
  open: boolean;
  listing?: ListingDetailDto | null;
  onClose: () => void;
  onSaved?: (saved: ListingDetailDto) => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const isEdit = Boolean(listing);

  const [form, setForm] = useState<ListingForm>(() => listingFormFrom(listing));

  // The agent the listing holds, as the server last returned it. `agentTouched`
  // separates "the specialist chose this" from "this is what the listing holds",
  // so a refetch while the modal is open never leaves a stale name on screen.
  const savedAgentId = listing?.listingAgentId ?? "";
  const savedAgent = listing?.listingAgent ?? null;
  const [agentChoice, setAgentChoice] = useState<ListingAgentDto | null>(savedAgent);
  const [agentTouched, setAgentTouched] = useState(false);
  const [creatingAgent, setCreatingAgent] = useState(false);

  // Keyed reset rather than a hydrate effect — reopening on a different listing
  // must not leave the previous one's values in the boxes. Keyed on the id, not
  // the object: creating an agent from + NEW AGENT refreshes every listing
  // query, and a reset on the object would drop the agent just selected.
  const currentKey = listing?.id ?? "new";
  const [formKey, setFormKey] = useState(currentKey);
  if (currentKey !== formKey) {
    setFormKey(currentKey);
    setForm(listingFormFrom(listing));
    setAgentChoice(listing?.listingAgent ?? null);
    setAgentTouched(false);
    setCreatingAgent(false);
  }

  const chooseAgent = (next: ListingAgentDto | null) => {
    setAgentTouched(true);
    setAgentChoice(next);
  };

  const shownAgent = agentTouched ? agentChoice : savedAgent;
  // Untouched, the listing keeps the agent it has — including one that has
  // since left the directory and so cannot be shown. An unrelated edit must
  // never silently detach them.
  const agentIdToSave = agentTouched ? (agentChoice?.id ?? "") : savedAgentId;

  const numberErrors = listingNumberErrors(form);
  const canSave = Object.keys(numberErrors).length === 0;

  // 🔴 Advisory only, and only while creating. The duplicate probe never gates
  // Save — see ListingDuplicateWarning.
  const duplicates = useListingDuplicates({
    rawUrl: form.rawUrl,
    businessName: form.businessName,
    excludeId: listing?.id,
    enabled: open,
  });
  const matches = duplicates.data?.matches ?? [];

  const save = useMutation({
    mutationFn: () => {
      if (listing) {
        const patch: UpdateListingPayload = listingToPayload(form, { forEdit: true });
        if (agentIdToSave) {
          patch.listingAgentId = agentIdToSave;
        } else if (listing.listingAgentId) {
          // A nullable id cannot express "detach" on its own, since bare null
          // already means unchanged.
          patch.clearListingAgent = true;
        }
        return listingsApi.update(listing.id, patch);
      }
      const body: CreateListingPayload = listingToPayload(form, { forEdit: false });
      // 🔴 Always an id, never `newAgent`. A new agent is created by the shared
      // New agent form BEFORE this save, strictly and with the duplicate check
      // on screen, so the listing only ever attaches an agent that already
      // exists. `newAgent` stays on the API for the paths that still use it.
      if (agentIdToSave) body.listingAgentId = agentIdToSave;
      // Records in the audit trail that a warning was on screen and the
      // specialist went ahead — it does not unlock anything, because nothing
      // was locked.
      if (matches.length > 0) body.confirmCreateDespiteDuplicate = true;
      return listingsApi.create(body);
    },
    onSuccess: (saved) => {
      void qc.invalidateQueries({ queryKey: listingKeys.all });
      if (!isEdit) {
        // NEW LISTING stays mounted between uses and its reset key never
        // changes in create mode, so without this the next listing would open
        // holding the last one's details and agent.
        setForm(listingFormFrom(null));
        setAgentChoice(null);
        setAgentTouched(false);
        setCreatingAgent(false);
      }
      toast.success(isEdit ? "Listing updated." : "Listing created.");
      onSaved?.(saved);
      onClose();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        title={isEdit ? "Edit listing" : "New listing"}
        size="lg"
        footer={
          <>
            <Button variant="text" onClick={onClose}>
              Cancel
            </Button>
            <Button disabled={save.isPending || !canSave} onClick={() => save.mutate()}>
              {save.isPending
                ? "Saving…"
                : matches.length > 0
                  ? isEdit
                    ? "Save anyway"
                    : "Create anyway"
                  : isEdit
                    ? "Save listing"
                    : "Create listing"}
            </Button>
          </>
        }
      >
        <div className="grid gap-space-4">
          {matches.length > 0 && (
            <ListingDuplicateWarning
              matches={matches}
              normalizedUrl={duplicates.data?.normalizedUrl}
            />
          )}

          <ListingFieldsBlock value={form} onChange={setForm} errors={numberErrors} isEdit={isEdit} />

          <div className="grid gap-space-3 border-t border-border-light pt-space-4">
            <ListingAgentPicker
              label="Listing agent"
              value={shownAgent}
              onChange={chooseAgent}
              allowNone
              onNewAgent={() => setCreatingAgent(true)}
              disabled={save.isPending}
              testIdPrefix="listing-form-agent"
              unresolvedLabel={
                !agentTouched && savedAgentId && !savedAgent
                  ? "The agent on this listing is no longer in the directory."
                  : undefined
              }
            />

            {isEdit && savedAgentId && !agentIdToSave && (
              <p className="text-caption text-gray-mid m-0">
                Saving will detach the current agent. The agent record itself is
                shared and stays on file.
              </p>
            )}
          </div>
        </div>
      </Modal>

      {/*
        A SIBLING of the listing modal, never nested inside it: the New agent
        form has its own Save, and a form inside a form would submit both. It
        opens over the listing modal, Escape closes only it, and focus returns
        to + NEW AGENT. The agent it creates — or the one picked from "Already
        on file?" — is SELECTED here; nothing attaches it until this modal is
        saved. A created agent stays on file even if the listing is cancelled.
      */}
      <ListingAgentEditModal
        open={open && creatingAgent}
        onClose={() => setCreatingAgent(false)}
        onSaved={chooseAgent}
        onUseExisting={(existing) => {
          chooseAgent(existing);
          setCreatingAgent(false);
        }}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// Duplicates — DETECT, WARN, NEVER BLOCK
// ---------------------------------------------------------------------------

/**
 * Suspected duplicates for a listing being created.
 *
 * <p>🔴 <b>Never blocks.</b> Two marketplaces list the same business, a broker
 * relists after a price cut, and a specialist deliberately re-enters one they
 * know about — a probe that matches on a normalised URL and a business name
 * cannot tell those apart from a mistake, so it reports and the specialist
 * decides. The server agrees: <code>confirmCreateDespiteDuplicate</code> is
 * recorded in the audit trail and gates nothing, so refusing here would be a
 * client-only wall in front of an endpoint that never asked for one.</p>
 */
export function useListingDuplicates({
  rawUrl,
  businessName,
  excludeId,
  enabled,
}: {
  rawUrl: string;
  businessName: string;
  excludeId?: string;
  enabled: boolean;
}) {
  const [debounced, setDebounced] = useState({ rawUrl: "", businessName: "" });

  useEffect(() => {
    const t = window.setTimeout(
      () => setDebounced({ rawUrl: rawUrl.trim(), businessName: businessName.trim() }),
      400,
    );
    return () => window.clearTimeout(t);
  }, [rawUrl, businessName]);

  const hasProbe = Boolean(debounced.rawUrl || debounced.businessName);

  return useQuery({
    queryKey: listingKeys.duplicateCheck(
      debounced.rawUrl,
      debounced.businessName,
      excludeId,
    ),
    queryFn: () =>
      listingsApi.duplicateCheck({
        rawUrl: debounced.rawUrl || undefined,
        businessName: debounced.businessName || undefined,
        excludeId,
      }),
    enabled: enabled && hasProbe,
    // Advisory: a failed probe must leave the form fully usable rather than
    // retrying behind a warning nobody can see.
    retry: false,
  });
}

export function ListingDuplicateWarning({
  matches,
  normalizedUrl,
}: {
  matches: ListingDuplicateDto[];
  normalizedUrl?: string | null;
}) {
  return (
    <div className="rounded-md border border-status-warning bg-white-warm px-space-3 py-space-2" role="status">
      <p className="flex items-center gap-space-1 text-small font-semibold text-onyx m-0">
        <AlertTriangle size={14} className="text-status-warning" aria-hidden />
        Possible duplicate{matches.length > 1 ? "s" : ""} — you can still create this:
      </p>
      <ul className="list-none p-0 m-0 mt-space-1 space-y-space-1">
        {matches.map((m) => (
          <li key={m.id} className="flex flex-wrap items-center gap-space-2 text-caption text-onyx">
            {/* SHORT — a duplicate-match row that already carries what it
                matched on, the marketplace and the asking price on the same
                line, and wraps once it runs out of room. */}
            <span
              className="font-semibold"
              title={listingNameOrFallback(m.displayName, m.businessName)}
            >
              {listingShortNameOrFallback(m.displayName, m.businessName)}
            </span>
            <span className="text-gray-mid">matched on {m.matchedOn}</span>
            {m.marketplaceName && <span className="text-gray-mid">· {m.marketplaceName}</span>}
            {m.askingPrice != null && (
              <span className="text-gray-mid">· {formatCurrency(m.askingPrice)}</span>
            )}
            <a
              href={`/listings/${m.id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold text-gold-shadow hover:underline"
            >
              Open existing
            </a>
          </li>
        ))}
      </ul>
      {normalizedUrl && (
        // Shown deliberately: a specialist told "this looks like a duplicate"
        // can only agree or disagree if they can see WHAT was compared.
        <p className="text-caption text-gray-mid mt-space-1 mb-0 break-all">
          Compared on {normalizedUrl}
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared listing field block — this modal and the paste-to-create confirm step
// ---------------------------------------------------------------------------

export interface ListingForm {
  rawUrl: string;
  businessName: string;
  headline: string;
  description: string;
  industry: string;
  marketplaceName: string;
  listingReference: string;
  city: string;
  state: string;
  zip: string;
  askingPrice: string;
  cashFlow: string;
  revenue: string;
  inventory: string;
  realEstateValue: string;
  yearEstablished: string;
  employeeCount: string;
  notes: string;
}

export const EMPTY_LISTING_FORM: ListingForm = {
  rawUrl: "",
  businessName: "",
  headline: "",
  description: "",
  industry: "",
  marketplaceName: "",
  listingReference: "",
  city: "",
  state: "",
  zip: "",
  askingPrice: "",
  cashFlow: "",
  revenue: "",
  inventory: "",
  realEstateValue: "",
  yearEstablished: "",
  employeeCount: "",
  notes: "",
};

/** Every numeric field, in one place — the validator and the payload builder
 *  must agree on the set or a typo can be reported and then silently dropped. */
const NUMBER_KEYS = [
  "askingPrice",
  "cashFlow",
  "revenue",
  "inventory",
  "realEstateValue",
  "yearEstablished",
  "employeeCount",
] as const;

type NumberKey = (typeof NUMBER_KEYS)[number];

export function listingFormFrom(
  source?: Partial<Record<keyof ListingForm, unknown>> | ListingDetailDto | null,
): ListingForm {
  if (!source) return EMPTY_LISTING_FORM;
  const s = source as Record<string, unknown>;
  const str = (key: string) => (s[key] == null ? "" : String(s[key]));
  return {
    rawUrl: str("rawUrl"),
    businessName: str("businessName"),
    headline: str("headline"),
    description: str("description"),
    industry: str("industry"),
    marketplaceName: str("marketplaceName"),
    listingReference: str("listingReference"),
    city: str("city"),
    state: str("state"),
    zip: str("zip"),
    askingPrice: str("askingPrice"),
    cashFlow: str("cashFlow"),
    revenue: str("revenue"),
    inventory: str("inventory"),
    realEstateValue: str("realEstateValue"),
    yearEstablished: str("yearEstablished"),
    employeeCount: str("employeeCount"),
    notes: str("notes"),
  };
}

/**
 * `$1,450,000` is what a marketplace page shows and what a specialist pastes.
 *
 * <p>Exported so the per-section Overview modals parse a figure the same way
 * this form and the two paste flows do. A second copy would be a second answer
 * to "is `1.2e` a number", and the one that drifts is whichever nobody is
 * looking at.</p>
 */
export function parseListingNumber(raw: string): number | undefined {
  const cleaned = raw.replace(/[$,\s]/g, "");
  if (!cleaned) return undefined;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : undefined;
}

/** A typed figure that does not parse is reported, never silently dropped. */
export function listingNumberErrors(form: ListingForm): Partial<Record<NumberKey, string>> {
  const errors: Partial<Record<NumberKey, string>> = {};
  for (const key of NUMBER_KEYS) {
    const raw = form[key];
    if (raw.trim() && parseListingNumber(raw) === undefined) {
      errors[key] = "Enter a number.";
    }
  }
  return errors;
}

export function listingToPayload(
  form: ListingForm,
  { forEdit }: { forEdit: boolean },
): ListingWritePayload {
  // PATCH reads ""=clear, so a hydrated edit form sends every string; POST has
  // nothing to clear, so blanks are omitted.
  const v = (raw: string) => (forEdit ? raw.trim() : raw.trim() || undefined);
  return {
    rawUrl: v(form.rawUrl),
    businessName: v(form.businessName),
    headline: v(form.headline),
    description: v(form.description),
    industry: v(form.industry),
    marketplaceName: v(form.marketplaceName),
    listingReference: v(form.listingReference),
    city: v(form.city),
    state: v(form.state),
    zip: v(form.zip),
    askingPrice: parseListingNumber(form.askingPrice),
    cashFlow: parseListingNumber(form.cashFlow),
    revenue: parseListingNumber(form.revenue),
    inventory: parseListingNumber(form.inventory),
    realEstateValue: parseListingNumber(form.realEstateValue),
    yearEstablished: parseListingNumber(form.yearEstablished),
    employeeCount: parseListingNumber(form.employeeCount),
    notes: v(form.notes),
  };
}

export function ListingFieldsBlock({
  value,
  onChange,
  errors,
  isEdit,
}: {
  value: ListingForm;
  onChange: (next: ListingForm) => void;
  errors: Partial<Record<NumberKey, string>>;
  isEdit?: boolean;
}) {
  const set = <K extends keyof ListingForm>(key: K, next: ListingForm[K]) =>
    onChange({ ...value, [key]: next });

  return (
    <div className="grid gap-space-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-space-3">
        <FormField label="Business name" className="sm:col-span-2">
          <Input
            value={value.businessName}
            onChange={(e) => set("businessName", e.target.value)}
          />
        </FormField>
        <FormField label="Headline" className="sm:col-span-2">
          <Input value={value.headline} onChange={(e) => set("headline", e.target.value)} />
        </FormField>
        <FormField label="Listing URL" className="sm:col-span-2">
          <Input value={value.rawUrl} onChange={(e) => set("rawUrl", e.target.value)} />
        </FormField>
        <FormField label="Marketplace">
          <Input
            value={value.marketplaceName}
            onChange={(e) => set("marketplaceName", e.target.value)}
          />
        </FormField>
        <FormField label="Listing reference">
          <Input
            value={value.listingReference}
            onChange={(e) => set("listingReference", e.target.value)}
          />
        </FormField>
        <FormField label="Industry">
          <Input value={value.industry} onChange={(e) => set("industry", e.target.value)} />
        </FormField>
        <FormField label="City">
          <Input value={value.city} onChange={(e) => set("city", e.target.value)} />
        </FormField>
        <FormField label="State">
          <Select value={value.state} onChange={(e) => set("state", e.target.value)}>
            <option value="">—</option>
            {US_STATES.map((s) => (
              <option key={s.code} value={s.code}>
                {s.name} ({s.code})
              </option>
            ))}
          </Select>
        </FormField>
        <FormField label="ZIP">
          <Input value={value.zip} onChange={(e) => set("zip", e.target.value)} />
        </FormField>
      </div>

      <div className="grid gap-space-3 border-t border-border-light pt-space-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-space-3">
          <NumberField label="Asking price" k="askingPrice" value={value} set={set} errors={errors} />
          <NumberField label="Cash flow" k="cashFlow" value={value} set={set} errors={errors} />
          <NumberField label="Revenue" k="revenue" value={value} set={set} errors={errors} />
          <NumberField label="Inventory" k="inventory" value={value} set={set} errors={errors} />
          <NumberField
            label="Real estate"
            k="realEstateValue"
            value={value}
            set={set}
            errors={errors}
          />
          <NumberField
            label="Year established"
            k="yearEstablished"
            value={value}
            set={set}
            errors={errors}
          />
          <NumberField label="Employees" k="employeeCount" value={value} set={set} errors={errors} />
        </div>
        {isEdit && (
          // Honest about an asymmetry in the write contract: strings clear when
          // emptied, figures do not — the update reads a missing number as
          // "unchanged" and has no way to express "erase this". Without the
          // caption, blanking a wrong figure looks like it saved and did not.
          <p className="text-caption text-gray-mid m-0">
            Clearing a figure leaves the saved value as it is. Type the correct
            number instead.
          </p>
        )}
      </div>

      <div className="grid gap-space-3 border-t border-border-light pt-space-4">
        <FormField label="Description">
          <Textarea
            rows={4}
            value={value.description}
            onChange={(e) => set("description", e.target.value)}
          />
        </FormField>
        <FormField label="Notes">
          <Textarea rows={3} value={value.notes} onChange={(e) => set("notes", e.target.value)} />
        </FormField>
      </div>
    </div>
  );
}

function NumberField({
  label,
  k,
  value,
  set,
  errors,
}: {
  label: string;
  k: NumberKey;
  value: ListingForm;
  set: <K extends keyof ListingForm>(key: K, next: ListingForm[K]) => void;
  errors: Partial<Record<NumberKey, string>>;
}) {
  return (
    <FormField label={label} error={errors[k]}>
      <Input
        inputMode="decimal"
        value={value[k]}
        invalid={Boolean(errors[k])}
        onChange={(e) => set(k, e.target.value)}
      />
    </FormField>
  );
}
